import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { ChevronRightIcon, CheckIcon } from "../components/ui/Icons";

export function SignupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const { signup, isSigningUp, signupError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    signup(
      { name, email, password },
      {
        onSuccess: () => navigate("/dashboard", { replace: true }),
      }
    );
  };

  const displayError = error || signupError?.message;

  const features = [
    "Share your plugins with the community",
    "Track downloads and user engagement",
    "Get feedback and ratings from users",
    "Monetize your creations (coming soon)",
  ];

  return (
    <div className="relative flex min-h-screen">
      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-30" />
      </div>

      {/* Left side - Signup form */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-12 lg:px-12">
        {/* Mobile logo */}
        <div className="mb-8 flex lg:hidden">
          <Link to="/" className="flex items-center">
            <span className="font-display text-lg font-medium tracking-tight text-secondary">
              plugins for <span className="text-amber">claude code</span>
            </span>
          </Link>
        </div>

        <div className="mx-auto w-full max-w-md">
          <div className="mb-8">
            <h2 className="font-display text-2xl font-bold tracking-tight text-primary">
              Create your account
            </h2>
            <p className="mt-2 text-secondary">Join the community of plugin developers</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {displayError && (
              <div className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
                {displayError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="mb-2 block font-mono text-sm text-secondary">
                  Full name
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="input-field"
                />
              </div>

              <div>
                <label htmlFor="email" className="mb-2 block font-mono text-sm text-secondary">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="input-field"
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block font-mono text-sm text-secondary">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input-field"
                />
                <p className="mt-1.5 font-mono text-xs text-muted">Must be at least 8 characters</p>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="mb-2 block font-mono text-sm text-secondary"
                >
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input-field"
                />
              </div>
            </div>

            <button type="submit" disabled={isSigningUp} className="btn-primary w-full">
              {isSigningUp ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Create account
                  <ChevronRightIcon size={16} />
                </span>
              )}
            </button>

            <p className="text-center font-mono text-xs text-muted">
              By signing up, you agree to our{" "}
              <Link to="/terms" className="text-amber hover:text-amber-hover">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-amber hover:text-amber-hover">
                Privacy Policy
              </Link>
            </p>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted">
              Already have an account?{" "}
              <Link to="/login" className="font-medium text-amber hover:text-amber-hover">
                Sign in
              </Link>
            </p>
          </div>

          {/* Divider */}
          <div className="my-8 flex items-center gap-4">
            <div className="h-px flex-1 bg-elevated" />
            <span className="font-mono text-xs text-muted">or continue with</span>
            <div className="h-px flex-1 bg-elevated" />
          </div>

          {/* Social logins */}
          <div className="grid grid-cols-2 gap-3">
            <button className="btn-secondary justify-center py-2.5">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
              </svg>
              GitHub
            </button>
            <button className="btn-secondary justify-center py-2.5">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden border-l border-elevated p-12 lg:flex">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <span className="font-display text-xl font-medium tracking-tight text-secondary">
            plugins for <span className="text-amber">claude code</span>
          </span>
        </Link>

        {/* Hero content */}
        <div className="max-w-md">
          <h1 className="mb-6 font-display text-4xl font-bold leading-tight tracking-tight">
            Start building
            <br />
            <span className="text-gradient">amazing plugins</span>
            <br />
            today
          </h1>
          <p className="mb-8 text-lg leading-relaxed text-secondary">
            Join thousands of developers creating plugins that enhance the Claude Code experience.
          </p>

          {/* Features list */}
          <ul className="space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber/20">
                  <CheckIcon size={14} className="text-amber" />
                </div>
                <span className="text-secondary">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Stats decoration */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-xl border border-elevated bg-surface/80 p-4 text-center">
            <p className="font-display text-2xl font-bold text-amber">2.5K+</p>
            <p className="font-mono text-xs text-muted">Plugins</p>
          </div>
          <div className="rounded-xl border border-elevated bg-surface/80 p-4 text-center">
            <p className="font-display text-2xl font-bold text-amber">50K+</p>
            <p className="font-mono text-xs text-muted">Downloads</p>
          </div>
          <div className="rounded-xl border border-elevated bg-surface/80 p-4 text-center">
            <p className="font-display text-2xl font-bold text-amber">1.2K+</p>
            <p className="font-mono text-xs text-muted">Developers</p>
          </div>
        </div>
      </div>
    </div>
  );
}
